import { TooltipProps } from 'lego-on-react';
import { Example } from './model/common';
import { Timezone } from './model/time';
import { Channel, InstallableApp, Skill } from './model/skill';
import { Operation } from './model/operation';
import { UpdateOAuthAppSettings } from './model/oauth';
import { ThereminvoxSoundMeta } from './model/thereminvox';
import { SkillAccess } from './model/settings';
import * as languagesKeyset from 'i18n/Language';
import * as regionsKeyset from 'i18n/Region';

// todo: why not use standard Omit?
export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export type EmailConfirmationStage = 'pending' | 'success' | 'failure' | 'not-modified';

export interface Snapshot {
    skills: Skill[];
    timezones: Timezone[];
    operations: Operation[];
    categories: AliceSkillCategory[];
    maxSkillsPerUser: number;
    voices: TtsVoice[];
    regionsWithSufficientLanguages: RegionSufficientLanguage[];
}

export interface AppStoreSnapshot {
    installableApps: InstallableApp[];
    maxAppsPerUser: number;
    targetPlatformsByChannel: { [key in Channel]?: string[] };
    categories: AliceSkillCategory[];
    ageRestrictions: string[];
    // TODO:
    // timezones: Timezone[];
    // operations: Operation[];
}

export interface TtsVoice {
    name: string;
    title: string;
    samples: Array<{
        src: string;
        type: string;
    }>;
}

export interface AliceSkillCategory {
    type: string;
    title: string;
    isDefault: boolean;
}

interface CommonFormSettings {
    brandVerificationWebsite: string;
    developerName: string;
    logoId: string | null;
    name: string;
    email: string;
    hideInStore: boolean;
}

export type BackendType = 'webhook' | 'function';

export interface AliceSkillFormSettings extends CommonFormSettings {
    activationPhrases: string[];
    description: string;
    backendType: BackendType;
    uri: string;
    functionId: string;
    structuredExamples: Example[];
    skillAccess?: SkillAccess;
    noteForModerator: string;
    requiredInterfaces: string[];
    exactSurfaces: string[];
    surfaceWhitelist: string[];
    surfaceBlacklist: string[];
    voice: string;
    yaCloudGrant: boolean;
    explicitContent: boolean;
    oauthAppId: string;
    category: string;
    appMetricaApiKey: string;
    useStateStorage: boolean;
    rsyPlatformId: string;
}

export type AliceSkillFormSettingsField = keyof AliceSkillFormSettings;

export type LanguageCode = keyof typeof languagesKeyset.en;
export type RegionCode = keyof typeof regionsKeyset.en;

export interface SupportedLanguageFormData {
    languageCode?: LanguageCode;
    languageDisplayName?: string;
    appName?: string;
    subtitle?: string;
    description?: string;

    externalSettingsDescription?: string;
    supportedUnitsDescription?: string;
    supportServiceUrl?: string;
    supportServiceUrlIsRequired?: boolean;
}

export interface RegionSufficientLanguage {
    regionCode: RegionCode;
    sufficientLanguageCodes: LanguageCode[];
}

export type ErrorForLanguageForm = {
    [Property in keyof SupportedLanguageFormData]?: string;
};

export interface SmartHomeFormSettings extends CommonFormSettings {
    description: string;
    secondaryTitle: string;

    backendType: BackendType;
    uri: string;
    functionId: string;
    noteForModerator: string;
    voice: string;
    oauthAppId: string;
    androidDeepLink: string;
    iosDeepLink: string;
    iosDeepLinkFallbackURL: string;
    category: string;
    isTrustedSmartHomeSkill: boolean;
    skillAccess?: SkillAccess;
    supportedLanguages: SupportedLanguageFormData[];
    enableAllAvailableRegions: boolean;
    selectedRegions: RegionCode[];
}

export type AgeRestriction = '0+' | '6+' | '12+' | '16+' | '18+';

export interface InstallableAppFormSettings extends CommonFormSettings {
    activationPhrases: string[];
    packageName: string;
    noteForModerator: string;
    description: string;
    category: string;
    isTrusted: boolean;
    ageRestriction?: AgeRestriction;
    apkInfoWithTargets: Partial<AppVersionInfoWithTargetPlatforms>;
    secondaryTitle: string;
    eulaAgreement?: LicenseAgreement;
    privacyAgreement?: LicenseAgreement;
    screenshots?: InstallableAppScreenshot[];
    skillAccess: SkillAccess;
}

export type LicenseAgreement = {
    url: string;
};

export type InstallableAppScreenshot = {
    imageUrl: string;
    imageId: string;
};

export type AppVersionInfo = {
    appVersionId: string;
    url: string;
    versionNotes?: string;
    versionCode: number;
    versionName: string;
};

export type AppVersionInfoWithTargetPlatforms = AppVersionInfo & { targetPlatforms?: string[] };

export type SmartHomeFormSettingsField = keyof SmartHomeFormSettings;

export interface ThereminvoxFormSettings {
    hideInStore: boolean;
    name: string;
    soundsMeta: ThereminvoxSoundMeta[];
    repeatSoundInside: boolean;
    noOverlaySamples: boolean;
    stopOnCeil: boolean;
}

export type ThereminvoxFormSettingsField = keyof ThereminvoxFormSettings;

export type UpdateOAuthAppSettingsField = keyof UpdateOAuthAppSettings;

export type AnyFormSettings = AliceSkillFormSettings | SmartHomeFormSettings | ThereminvoxFormSettings;

export enum PhraseCommonness {
    Green = 'green',
    Red = 'red',
}

export interface NewsResponse {
    id: string;
    url: string;
    title: string;
    publishDate: string;
    newsTags: string[];
}

export interface TooltipState {
    tooltipVisible: boolean;
    tooltipTheme?: TooltipProps['theme'];
    tooltipLabel?: string;
}

export type BaseTooltipAction = 'ok' | 'error' | 'clear';

export enum PublishingPageDangerousOperation {
    Stop = 'stop',
    Delete = 'delete',
}

export interface FeatureFlags {
    skill_feature_flags: string[];
    user_feature_flags: Record<string, boolean>;
}
